import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, Calendar, User, FileText, Eye, Check, AlertTriangle, Minus, Plus,
  CheckSquare, Upload, Trash2, ChevronRight, History, Edit3, ShieldCheck, ChevronDown,
} from 'lucide-react';
import { Button, Modal, DocumentViewer } from '../../../components';
import { qualityService } from '../../../services';
import {
  ANSWERS, Badge, Card, Notice, auditStatus, actionStatus,
  formatDate, formatSize, fileUrl, inputClass, errorMessage, FieldLabel,
} from './shared';
import ActionFormModal from './ActionFormModal';

/**
 * The audit workspace — where the checking actually happens.
 *
 * Three ideas run through the whole screen:
 *
 * 1. **Answering is one click, explaining is required.** Met / Needs action /
 *    N/A saves immediately. But a "Needs action" or "N/A" with no note is a
 *    finding nobody can act on later, so the note opens automatically and
 *    sign-off refuses without it. For "Met" the note is genuinely optional and
 *    stays folded away.
 *
 * 2. **A finding must become somebody's job.** Every "Needs action" shows
 *    either its linked follow-up or a button to assign one, right there under
 *    the answer — not on another screen the manager has to remember to visit.
 *
 * 3. **Completed is read-only.** Every control disappears and the API refuses
 *    the write anyway. A signed-off audit is evidence of what was found on the
 *    day, and evidence that can be edited afterwards is not evidence.
 *
 * Notes autosave as they are typed (debounced) because losing a half-written
 * finding to a stray click is the fastest way to make someone stop writing
 * them properly.
 */
const AuditWorkspace = ({ auditId, meta, onBack, onOpenActions, onOpenPolicy, onEdit, onChanged }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const [actionModal, setActionModal] = useState(null);   // { action?, question? }
  const [addCheckOpen, setAddCheckOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [viewFile, setViewFile] = useState(null);

  const noteTimers = useRef({});
  const fileInput = useRef(null);

  const done = audit?.status === 'completed';

  const load = useCallback(async () => {
    try {
      const response = await qualityService.getAudit(auditId);
      setAudit(response.data);
      // Seed the local note drafts from the server exactly once per load, so
      // an in-flight autosave never overwrites what is being typed.
      setNotes(Object.fromEntries((response.data.questions || []).map((q) => [q.id, q.note || ''])));
    } catch (error) {
      toast.error(errorMessage(error, 'This audit could not be opened.'));
      onBack();
    } finally {
      setLoading(false);
    }
  }, [auditId, onBack]);

  useEffect(() => { load(); }, [load]);

  // Flush nothing on unmount — the debounce is short and a pending note would
  // race the reload. Clearing the timers just stops a setState after unmount.
  useEffect(() => {
    const timers = noteTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // Just the local record. The register behind this screen is deliberately NOT
  // refreshed on every answer: it isn't visible, and refetching it on each of
  // twenty clicks would be twenty wasted requests. It is reloaded when the
  // manager goes back to it (see onBack in AuditsPolicies).
  const apply = (data) => setAudit(data);

  const answer = async (questionId, value) => {
    if (done) return;
    // Optimistic: the answer buttons must feel instant, and a failure restores
    // from the server response either way.
    setAudit((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.id === questionId ? { ...q, answer: value } : q)),
    }));
    try {
      const response = await qualityService.answerCheck(auditId, questionId, { answer: value });
      apply(response.data);
    } catch (error) {
      toast.error(errorMessage(error, 'That answer could not be saved.'));
      load();
    }
  };

  const changeNote = (questionId, value) => {
    setNotes((prev) => ({ ...prev, [questionId]: value }));
    clearTimeout(noteTimers.current[questionId]);
    noteTimers.current[questionId] = setTimeout(async () => {
      try {
        setSaving(true);
        await qualityService.answerCheck(auditId, questionId, { note: value });
      } catch (error) {
        toast.error(errorMessage(error, 'That note could not be saved.'));
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const uploadEvidence = async (files) => {
    if (!files.length) return;
    try {
      const response = await qualityService.addEvidence(auditId, files);
      // Per-file problems (too large, already attached) come back as `skipped`
      // rather than failing the whole upload — one bad file should not lose
      // the four good ones beside it.
      (response.skipped || []).forEach((message) => toast.error(message));
      if (response.data) apply(response.data);
      if (response.skipped?.length !== files.length) toast.success(response.message);
    } catch (error) {
      toast.error(errorMessage(error, 'Those files could not be attached.'));
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removeEvidence = async (file) => {
    if (!window.confirm(`Remove ${file.name} from this audit?`)) return;
    try {
      const response = await qualityService.removeEvidence(auditId, file.id);
      apply(response.data);
      toast.success('Attachment removed.');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const removeCheck = async (question) => {
    if (!window.confirm('Remove this check from the audit?')) return;
    try {
      const response = await qualityService.removeCheck(auditId, question.id);
      apply(response.data);
      toast.success('Check removed.');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const actionsFor = useCallback(
    (questionId) => (audit?.actions || []).filter((a) => a.question_id === questionId),
    [audit],
  );

  const openActionCount = useMemo(
    () => (audit?.actions || []).filter((a) => a.status === 'open').length,
    [audit],
  );

  if (loading) {
    return <div className="py-20 text-center text-[13px] text-[#64748B] dark:text-gray-400">Loading audit…</div>;
  }
  if (!audit) return null;

  const status = auditStatus(audit);

  return (
    <div className="max-w-[1080px]">
      <div className="flex items-center gap-2.5 text-[12px] text-[#8192AC] dark:text-gray-500 mb-5">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline">
          <ArrowLeft className="w-4 h-4" /> All audits
        </button>
        <span>/</span>
        <span>{done ? 'Completed audit' : 'Audit workspace'}</span>
      </div>

      <header className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[23px] font-extrabold tracking-[-0.5px] text-[#0B1220] dark:text-gray-100">{audit.title}</h1>
            <Badge tone={status.tone}>{status.label}</Badge>
          </div>
          <div className="flex items-center gap-5 flex-wrap text-[12px] text-[#64748B] dark:text-gray-400 mt-2.5">
            <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{audit.owner_name || 'Unassigned'}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Due {formatDate(audit.due_date)}</span>
            <span>{audit.repeat_cycle}</span>
          </div>
        </div>
        <Button
          variant="secondary"
          leftIcon={done ? <History className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
          onClick={() => (done ? setHistoryOpen(true) : onEdit(audit))}
        >
          {done ? 'Activity' : 'Edit details'}
        </Button>
      </header>

      <div className="flex items-center justify-between gap-4 flex-wrap text-[12px] text-[#64748B] dark:text-gray-400 mb-5">
        {audit.policy ? (
          <button
            type="button"
            onClick={() => onOpenPolicy(audit.policy.id)}
            className="inline-flex items-center gap-2 text-blue-600 hover:underline"
          >
            <FileText className="w-4 h-4" />
            {audit.policy.title}
            {audit.policy_version ? ` · v${audit.policy_version}` : ''}
            <Eye className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span>No linked policy · Standalone audit</span>
        )}
        <span className="tabular-nums">{audit.answered} of {audit.total_checks} checks answered</span>
      </div>

      {done && (
        <Notice tone="green" className="mb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <strong className="block">Completed by {audit.signed_by_name} · {formatDate(audit.completed_at)}</strong>
              <p className="mt-1 whitespace-pre-wrap">{audit.summary}</p>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setReviewOpen(true)}>View summary</Button>
          </div>
        </Notice>
      )}

      {/* ------------------------------------------------------ checklist - */}
      <Card className="mb-5">
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-[#EEF2F8] dark:border-gray-800">
          <div>
            <h2 className="text-[16px] font-bold text-[#0B1220] dark:text-gray-100">Checklist</h2>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1">
              {done ? 'Recorded answers and findings.' : 'Choose an answer for each check. Add details where needed.'}
            </p>
          </div>
          <span className="text-[13px] font-semibold text-[#64748B] dark:text-gray-400 tabular-nums shrink-0">
            {audit.answered} / {audit.total_checks}
          </span>
        </div>

        <div>
          {audit.questions.map((question, index) => {
            const linked = actionsFor(question.id);
            const needsAttention = question.answer === 'needs';
            const requiresNote = ['needs', 'na'].includes(question.answer);

            return (
              <article
                key={question.id}
                className={`px-6 py-5 border-b border-[#EEF2F8] dark:border-gray-800 last:border-b-0 ${
                  needsAttention ? 'bg-amber-50/40 dark:bg-amber-900/5' : ''
                }`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <span className="text-[11px] font-bold text-[#94A3B8] dark:text-gray-500 pt-1.5 tabular-nums min-w-[16px]">
                    {index + 1}
                  </span>
                  <h3 className="flex-1 min-w-[240px] text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100 leading-relaxed pt-0.5">
                    {question.text}
                  </h3>

                  <div className="flex items-center gap-1.5" role="group" aria-label={`Answer for check ${index + 1}`}>
                    {ANSWERS.map(({ value, label }) => {
                      const Icon = { met: Check, needs: AlertTriangle, na: Minus }[value];
                      const selected = question.answer === value;
                      const tone = {
                        met: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300',
                        needs: 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300',
                        na: 'border-slate-400 bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300',
                      }[value];
                      return (
                        <button
                          key={value}
                          type="button"
                          disabled={done}
                          aria-pressed={selected}
                          onClick={() => answer(question.id, value)}
                          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[12px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-default ${
                            selected
                              ? tone
                              : 'border-[#E3E9F3] dark:border-gray-700 text-[#64748B] dark:text-gray-400 hover:bg-[#F5F8FC] dark:hover:bg-gray-800'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      );
                    })}
                    {!done && !question.answer && (
                      <button
                        type="button"
                        onClick={() => removeCheck(question)}
                        title="Remove this check"
                        aria-label={`Remove check ${index + 1}`}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-[#94A3B8] hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* A "Met" note is genuinely optional, so it stays folded. A
                    "Needs action" / "N/A" note is required, so it is open. */}
                {question.answer === 'met' && (
                  <details className="ml-7 mt-3 group">
                    <summary className="text-[12px] text-blue-600 cursor-pointer inline-flex items-center gap-1.5 list-none">
                      <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
                      {notes[question.id] ? 'View note' : 'Add a note'}
                    </summary>
                    <div className="mt-2.5">
                      {done ? (
                        <p className="text-[12.5px] text-[#475569] dark:text-gray-300 whitespace-pre-wrap">
                          {question.note || 'No note added.'}
                        </p>
                      ) : (
                        <textarea
                          className={inputClass}
                          rows={2}
                          value={notes[question.id] ?? ''}
                          onChange={(e) => changeNote(question.id, e.target.value)}
                          placeholder="Add a short note…"
                          aria-label={`Note for check ${index + 1}`}
                        />
                      )}
                    </div>
                  </details>
                )}

                {requiresNote && (
                  <div className="ml-7 mt-3">
                    <FieldLabel required={!done}>
                      {question.answer === 'needs' ? 'What needs attention?' : 'Why does this not apply?'}
                    </FieldLabel>
                    {done ? (
                      <p className="text-[12.5px] text-[#475569] dark:text-gray-300 whitespace-pre-wrap">
                        {question.note || 'No note added.'}
                      </p>
                    ) : (
                      <textarea
                        className={inputClass}
                        rows={2}
                        value={notes[question.id] ?? ''}
                        onChange={(e) => changeNote(question.id, e.target.value)}
                        placeholder="Describe what you found or why this does not apply…"
                        aria-label={`Finding for check ${index + 1}`}
                      />
                    )}

                    {needsAttention && (
                      <div className="mt-3">
                        {linked.length ? linked.map((action) => {
                          const state = actionStatus(action);
                          return (
                            <button
                              key={action.id}
                              type="button"
                              onClick={() => setActionModal({ action })}
                              className="w-full flex items-center gap-3 text-left border border-[#E3E9F3] dark:border-gray-700 rounded-xl px-3.5 py-3 mb-2 hover:bg-[#F8FAFD] dark:hover:bg-gray-800/40"
                            >
                              <CheckSquare className="w-4 h-4 text-blue-600 shrink-0" />
                              <span className="flex-1 min-w-0">
                                <span className="block text-[12.5px] font-semibold text-[#0B1220] dark:text-gray-100 truncate">
                                  {action.title}
                                </span>
                                <span className="block text-[11px] text-[#94A3B8] dark:text-gray-500 mt-0.5">
                                  {action.owner_name} · {formatDate(action.due_date)} · {state.label}
                                </span>
                              </span>
                              <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />
                            </button>
                          );
                        }) : !done && (
                          <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<Plus className="w-4 h-4" />}
                            onClick={() => setActionModal({ question })}
                          >
                            Assign follow-up action
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {!done && (
          <div className="px-6 py-4 border-t border-[#EEF2F8] dark:border-gray-800">
            <Button variant="ghost" size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setAddCheckOpen(true)}>
              Add a check
            </Button>
          </div>
        )}
      </Card>

      {/* --------------------------------------------- actions & evidence - */}
      <Card className="mb-5 divide-y divide-[#EEF2F8] dark:divide-gray-800">
        <Section
          icon={CheckSquare}
          title="Follow-up actions"
          subtitle={audit.total_actions
            ? `${openActionCount} open · ${audit.total_actions - openActionCount} completed`
            : 'Assign anything that needs attention'}
          badge={audit.total_actions ? `${audit.total_actions} ${audit.total_actions === 1 ? 'action' : 'actions'}` : null}
          defaultOpen={openActionCount > 0}
        >
          {(audit.actions || []).length ? (audit.actions || []).map((action) => {
            const state = actionStatus(action);
            return (
              <div key={action.id} className="border border-[#E3E9F3] dark:border-gray-700 rounded-xl p-4 mb-2.5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h4 className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">{action.title}</h4>
                    <div className="flex items-center gap-4 flex-wrap text-[11.5px] text-[#64748B] dark:text-gray-400 mt-2">
                      <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{action.owner_name}</span>
                      <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(action.due_date)}</span>
                      <Badge tone={state.tone}>{state.label}</Badge>
                    </div>
                    {action.status === 'done' && action.completion_note && (
                      <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-2.5 whitespace-pre-wrap">
                        {action.completion_note}
                      </p>
                    )}
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setActionModal({ action })}>
                    {action.status === 'done' ? 'View' : 'Update'}
                  </Button>
                </div>
              </div>
            );
          }) : (
            <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500">No actions added yet.</p>
          )}

          {!done && (
            <Button size="sm" variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setActionModal({})}>
              Add action
            </Button>
          )}
        </Section>

        <Section
          icon={Upload}
          title="Evidence"
          subtitle={audit.evidence.length
            ? `${audit.evidence.length} attached ${audit.evidence.length === 1 ? 'file' : 'files'}`
            : 'Attach documents or photos, if needed'}
          badge="Optional"
          defaultOpen={false}
        >
          {!done && (
            <label
              htmlFor="audit-evidence-input"
              className="flex flex-col items-center justify-center border-2 border-dashed border-[#D7E4FC] dark:border-gray-700 rounded-xl py-7 px-4 mb-3 cursor-pointer hover:bg-[#F8FAFD] dark:hover:bg-gray-800/40"
            >
              <Upload className="w-5 h-5 text-blue-600 mb-2" />
              <strong className="text-[13px] text-[#0B1220] dark:text-gray-100">Choose files</strong>
              <span className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 mt-1">PDF, JPG or PNG · Up to 10 MB each</span>
              <input
                id="audit-evidence-input"
                ref={fileInput}
                type="file"
                multiple
                accept="application/pdf,image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={(e) => uploadEvidence([...e.target.files])}
              />
            </label>
          )}

          {audit.evidence.map((file) => (
            <div key={file.id} className="flex items-center gap-3 border border-[#E3E9F3] dark:border-gray-700 rounded-xl px-3.5 py-3 mb-2">
              <FileText className="w-4 h-4 text-blue-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <strong className="block text-[12.5px] font-semibold text-[#0B1220] dark:text-gray-100 truncate">{file.name}</strong>
                <span className="block text-[11px] text-[#94A3B8] dark:text-gray-500 mt-0.5">
                  {formatSize(file.size)} · {formatDate(file.date)}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                icon={<Eye className="w-4 h-4" />}
                aria-label={`View ${file.name}`}
                onClick={() => setViewFile({ url: fileUrl(file.url), name: file.name, title: file.name })}
              />
              {!done && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4" />}
                  aria-label={`Remove ${file.name}`}
                  onClick={() => removeEvidence(file)}
                />
              )}
            </div>
          ))}

          {!audit.evidence.length && done && (
            <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500">No evidence files were attached.</p>
          )}
        </Section>
      </Card>

      {/* ---------------------------------------------------------- footer - */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-800 rounded-[16px] px-5 py-4">
        <span className="inline-flex items-center gap-2 text-[12px] text-[#64748B] dark:text-gray-400">
          {done ? <ShieldCheck className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {done ? 'Completed answers are read-only' : saving ? 'Saving…' : 'Answers and notes save as you work'}
        </span>
        <div className="flex items-center gap-2.5">
          <Button variant="secondary" onClick={onBack}>{done ? 'Back to audits' : 'Close'}</Button>
          {!done && (
            <Button variant="primary" rightIcon={<ChevronRight className="w-4 h-4" />} onClick={() => setReviewOpen(true)}>
              Review &amp; complete
            </Button>
          )}
        </div>
      </div>

      {actionModal && (
        <ActionFormModal
          open
          onClose={() => setActionModal(null)}
          meta={meta}
          audit={audit}
          action={actionModal.action}
          question={actionModal.question}
          onSaved={() => { load(); onChanged?.(); }}
        />
      )}

      <AddCheckModal
        open={addCheckOpen}
        onClose={() => setAddCheckOpen(false)}
        auditId={auditId}
        onSaved={apply}
      />

      <ReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        auditId={auditId}
        completed={done}
        onCompleted={() => { load(); onChanged?.(); }}
        onOpenActions={onOpenActions}
      />

      <Modal isOpen={historyOpen} onClose={() => setHistoryOpen(false)} title="Audit activity" maxWidth="max-w-lg">
        <ol className="space-y-3">
          {(audit.events || []).map((event, i) => (
            <li key={`${event.at}-${i}`} className="flex items-baseline justify-between gap-4 text-[12.5px]">
              <span className="text-[#334155] dark:text-gray-300">{event.text}</span>
              <span className="text-[11px] text-[#94A3B8] dark:text-gray-500 shrink-0">{formatDate(event.at)}</span>
            </li>
          ))}
          {!audit.events?.length && (
            <li className="text-[12.5px] text-[#94A3B8] dark:text-gray-500">Nothing recorded yet.</li>
          )}
        </ol>
      </Modal>

      <DocumentViewer file={viewFile} onClose={() => setViewFile(null)} />
    </div>
  );
};

/** A collapsible panel. Opened by default when it holds something that needs doing. */
const Section = ({ icon: Icon, title, subtitle, badge, defaultOpen, children }) => (
  <details open={defaultOpen} className="group">
    <summary className="flex items-center gap-3.5 px-6 py-4 cursor-pointer list-none">
      <span className="w-9 h-9 rounded-xl bg-[#EEF3FF] dark:bg-blue-900/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-blue-600" />
      </span>
      <span className="flex-1 min-w-0">
        <strong className="block text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">{title}</strong>
        <span className="block text-[11.5px] text-[#94A3B8] dark:text-gray-500 mt-0.5">{subtitle}</span>
      </span>
      {badge && <Badge>{badge}</Badge>}
      <ChevronDown className="w-4 h-4 text-[#94A3B8] transition-transform group-open:rotate-180 shrink-0" />
    </summary>
    <div className="px-6 pb-5">{children}</div>
  </details>
);

/** Add a check the template did not anticipate, without losing the answers so far. */
const AddCheckModal = ({ open, onClose, auditId, onSaved }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setText(''); setError(''); } }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await qualityService.addCheck(auditId, text.trim());
      onSaved(response.data);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={onClose} title="Add a checklist item" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1">Add one clear question to this audit.</p>
        {error && <Notice tone="red">{error}</Notice>}
        <div>
          <FieldLabel required>Check / question</FieldLabel>
          <textarea
            className={inputClass}
            rows={3}
            maxLength={400}
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Are fridge temperatures recorded twice daily?"
          />
        </div>
        <div className="flex items-center justify-end gap-2.5 pt-1">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" loading={saving}>Add check</Button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Review & sign off.
 *
 * The blockers come from the API, all of them at once, so the manager sees
 * everything standing between them and completion instead of discovering one
 * problem per attempt. The confirmation tickbox is not decoration: sign-off is
 * recorded against their name and the checklist becomes read-only.
 */
const ReviewModal = ({ open, onClose, auditId, completed, onCompleted, onOpenActions }) => {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setConfirmed(false);
    qualityService.reviewAudit(auditId)
      .then((response) => { setData(response.data); setSummary(response.data.summary || ''); })
      .catch((err) => setError(errorMessage(err)));
  }, [open, auditId]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await qualityService.completeAudit(auditId, { summary, confirmed });
      toast.success(response.message);
      onCompleted();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const blockers = data?.issues || [];
  const naCount = (data?.questions || []).filter((q) => q.answer === 'na').length;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={completed ? 'Audit summary' : 'Review & complete'}
      maxWidth="max-w-2xl"
    >
      {!data ? (
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 py-6 text-center">Loading…</p>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1">
            {completed
              ? 'The findings are saved. Follow-up actions remain separately tracked.'
              : 'Check the findings and confirm this audit is ready to complete.'}
          </p>

          {error && <Notice tone="red">{error}</Notice>}

          <div className="grid grid-cols-3 gap-3">
            {[
              ['Checks answered', `${data.answered} / ${data.total_checks}`],
              ['Checks met', data.score === null ? '—' : `${data.score}%`],
              ['Open actions', String(data.open_actions)],
            ].map(([label, value]) => (
              <div key={label} className="bg-[#F8FAFC] dark:bg-gray-800/40 border border-[#E3E9F3] dark:border-gray-800 rounded-xl px-4 py-3.5">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#94A3B8] dark:text-gray-500">{label}</div>
                <div className="text-[21px] font-extrabold text-[#0B1220] dark:text-gray-100 mt-1.5 tabular-nums">{value}</div>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 leading-relaxed">
            Score = checks met ÷ applicable checks. {naCount} marked N/A {naCount === 1 ? 'is' : 'are'} excluded.
            {data.score === null ? ' No applicable answered checks, so no score is shown.' : ''}
          </p>

          {!completed && blockers.length > 0 && (
            <Notice tone="amber">
              <strong className="block mb-1.5">Before you complete this audit</strong>
              <ul className="list-disc pl-5 space-y-1">
                {blockers.map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
            </Notice>
          )}

          {completed ? (
            <>
              <Notice tone="green">
                Completed by {data.signed_by_name} · {formatDate(data.completed_at)}
              </Notice>
              <p className="text-[13px] leading-relaxed text-[#334155] dark:text-gray-300 whitespace-pre-wrap">{data.summary}</p>
            </>
          ) : (
            <>
              <div>
                <FieldLabel required>Audit summary</FieldLabel>
                <textarea
                  className={inputClass}
                  rows={4}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Summarise the findings and any follow-up needed…"
                />
              </div>

              <label className="flex items-start gap-3 text-[12.5px] text-[#334155] dark:text-gray-300 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 accent-blue-600 shrink-0"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span>
                  I have reviewed the findings and assigned the follow-up actions. I am signing off this audit,
                  and the checklist will become read-only.
                </span>
              </label>
            </>
          )}

          {data.open_actions > 0 && (
            <Notice tone="blue">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span>
                  {data.open_actions} {data.open_actions === 1 ? 'action remains' : 'actions remain'} open.{' '}
                  {completed ? 'Continue tracking them in Actions.' : 'Completing the audit keeps these actions open until they are resolved.'}
                </span>
                <Button size="sm" variant="secondary" onClick={() => { onClose(); onOpenActions(auditId); }}>
                  View actions
                </Button>
              </div>
            </Notice>
          )}

          {!completed && data.next_due_date && (
            <Notice tone="blue">
              <span className="inline-flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                A new {String(data.repeat_cycle).toLowerCase()} audit will be scheduled for{' '}
                <strong>{formatDate(data.next_due_date)}</strong> when you complete this one.
              </span>
            </Notice>
          )}

          <div className="flex items-center justify-end gap-2.5 pt-1">
            <Button variant="secondary" type="button" onClick={onClose}>
              {completed ? 'Done' : 'Back to checklist'}
            </Button>
            {!completed && (
              <Button
                variant="success"
                type="submit"
                loading={saving}
                disabled={blockers.length > 0 || !confirmed || !summary.trim()}
                leftIcon={<Check className="w-4 h-4" />}
              >
                Complete audit
              </Button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AuditWorkspace;
